import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../login/AuthProvider";
import { dateHelper, dateListCreator } from "../../modules/helpers";
import { report, annual_target } from '../../components/apipath';
import { motion } from "framer-motion";
import ChartLine from "../../components/chart_line";
import ChartColumn from "../../components/chart_column";
import "./styles/adm.scss";

function AdmReport() {
  const [reportSums, setReportSums] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [annualTarget, setAnnualTarget] = useState(null);
  const { token } = useAuth();
  const [reportRows, setReportRows] = useState([]);
  const KPI = { cpa: 220, ea: 20, cma: 20 };

  const ytdRow = reportRows.find(r => r.period === '今年至今') || {};
  const doneCPA = Number(ytdRow.cpa || 0);
  const doneEA  = Number(ytdRow.ea  || 0);
  const doneCMA = Number(ytdRow.cma || 0);

  const pct = (done, target) => (target > 0 ? Math.min(100, (done / target) * 100) : 0);

  const pctCPA = pct(doneCPA, KPI.cpa);
  const pctEA  = pct(doneEA,  KPI.ea);
  const pctCMA = pct(doneCMA, KPI.cma);

  const timeOptions = [
    { label: '7 天', value: '7d' },
    { label: '30 天', value: '30d' },
    { label: '一季', value: '3M' },
    { label: '半年', value: '6M' },
    { label: '一年', value: '1y' },
  ];

 // ✨ 卡片與後端 series 順序對齊（共 16 張，0..15）
const data = [
  { index: 0,  title: "CPA 報名人數",   type: "column", unit: "人" },
  { index: 1,  title: "CMA 報名人數",   type: "column", unit: "人" },
  { index: 2,  title: "EA 報名人數",    type: "column", unit: "人" },

  // ✅ CSU 改成兩張
  { index: 3,  title: "CSU 金額",       type: "column", unit: "元", isRevenue: true },
  { index: 4,  title: "CSU 科目數",     type: "column", unit: "科", isCourse: true },

  // 後面全部順延 +1
  { index: 5,  title: "CPE 報名人數",   type: "column", unit: "人" },
  { index: 6,  title: "美稅 報名人數",  type: "column", unit: "人" },

  { index: 7,  title: "CPA 專業方案",   type: "column", unit: "人" },
  { index: 8,  title: "CPA 全修方案",   type: "column", unit: "人" },
  { index: 9,  title: "CPA 選修方案",   type: "column", unit: "人" },
  { index: 10, title: "CPA 自修方案",   type: "column", unit: "人" },
  { index: 11, title: "CPA 單科雙語",   type: "column", unit: "人" },
  { index: 12, title: "CMA 菁英方案",   type: "column", unit: "人" },
  { index: 13, title: "CMA 自修方案",   type: "column", unit: "人" },

  //CIA
  { index: 14, title: "CIA 自修方案",   type: "column", unit: "人" },

  // ⭐ 加購金額 index 14（原本 13）
  { index: 15, title: "加購金額",       type: "column", unit: "元", isRevenue: true },

  // ⭐ 成績單評估人數 index 15（原本 14）
  { index: 16, title: "成績單評估人數", type: "column", unit: "人" },
];

  // ✨ rangeMap 長度與卡片數一致
  const [rangeMap, setRangeMap] = useState(
    Array.from({ length: data.length }, () => '7d')
  );

  const getUserName = () => {
    const auth = JSON.parse(localStorage.getItem('authAdm'));
    return auth?.name || auth?.chinese_name || "使用者";
  };

  const roundTo = (num, decimal) => {
    return Math.round((num + Number.EPSILON) * Math.pow(10, decimal)) / Math.pow(10, decimal);
  };

  const dataMaker = (series, dateRange) => {
    if (!Array.isArray(series)) return [];
    const newData = [];
    for (let i = 0; i < dateRange.length; i++) {
      const day = dateRange[i];
      let y = 0;
      for (let j = 0; j < series.length; j++) {
        if (dateHelper(series[j].x, 'date') === day) {
          const val = Number(series[j].y) || 0;
          y = val === 0 ? 0.001 : val;
          break;
        }
      }
      newData.push({ x: i, y });
    }
    return newData;
  };

  const fetchWeeklySummary = async () => {
  const res = await axios.get(`${report}weekly-summary`, {
    headers: { token },
    });
    setReportRows(res.data?.data ?? []);
  };

  const fetchReportData = async () => {
    const queryString = rangeMap.map((val, idx) => `range${idx}=${val}`).join("&");
    const response = await axios.get(`${report}?${queryString}`, {
      headers: { token },
    });
    setReportData(response.data);
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeMap.join("|")]);

  const fetchAnnualTarget = async () => {
    const y = new Date().getFullYear();
    const res = await axios.get(`${annual_target}${y}`);
    setAnnualTarget(res.data?.data?.[0] ?? null);
  };

  useEffect(() => {
    fetchAnnualTarget();
    fetchWeeklySummary();
  }, []);

  // 預算圖表資料與合計
  const chartComputed = useMemo(() => {
    if (!reportData) return { charts: [], sums: [] };
    const charts = [];
    const sums = [];
    for (let i = 0; i < data.length; i++) {
      const series = reportData?.[i] ?? [];
      const dr = dateListCreator(rangeMap[i]);
      const cd = dataMaker(series, dr);
      charts.push(cd);
      const total = cd.reduce((acc, cur) => {
      const n = Number(cur.y);
      return acc + (Number.isFinite(n) ? n : 0);
      }, 0);
      sums.push(total);
    }
    return { charts, sums };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData, rangeMap.join("|")]);

  useEffect(() => {
    setReportSums(chartComputed.sums);
  }, [chartComputed.sums]);
  const dynCount = reportData ? Math.max(0, reportData.length - 4) : 0;

  const monthRevenue   = reportData?.[dynCount + 1]?.[0]?.total ?? 0;
  const quarterRevenue = reportData?.[dynCount + 2]?.[0]?.total ?? 0;
  const yearRevenue    = reportData?.[dynCount + 3]?.[0]?.total ?? 0;



  // 年度目標：只加「人數」卡
  const annualDone = data.reduce((acc, item, i) => {
    if (item.unit === '人') {
      const v = Number.isFinite(reportSums?.[i]) ? reportSums[i] : 0;
      return acc + v;
    }
    return acc;
  }, 0);

  const annualTargetNum = parseFloat(annualTarget?.target ?? 0);
  const progressPct = annualTargetNum > 0 ? Math.min(100, (annualDone / annualTargetNum) * 100) : 0;

  const handleAnnualTargetChange = async (e) => {
    const { value } = e.target;
    if (e.key === 'Enter') {
      const pattern = /^[1-9]\d*$/;
      if (!pattern.test(String(value))) {
        alert("年度目標必須是正整數!");
        return;
      }
      const ok = window.confirm("是否修改年度目標 ?");
      if (!ok) return;

      const y = new Date().getFullYear();
      const newData = {
        annual: y,
        target: value,
        complete_total: value,
        complete: Number(value) >= Number(annualTarget?.target ?? 0) ? 1 : 0,
      };
      try {
        await axios.put(annual_target, newData, {
          headers: { Authorization: `Bearer ${token}`, token }
        });
        fetchAnnualTarget();
      } catch (err) {
        alert(err);
      }
    }
  };

  return (
    <>
      {/* banner */}
      <div className="beckerbanner">
        <div className="beckerbannerblock col-4">
          <div className="wlecomsign">
            <div className="lgtext">Welcome</div>
            <div className="beclertitle">{getUserName()}</div>
            Thank you for your contribution<br />
            to Direct Line<br />
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, x: 100 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, default: { ease: "linear" } }}
        >
          <div className="col-10">
            <img src="/admImg/welcomepic.jpg" alt="" />
          </div>
        </motion.div>
      </div>
      

      <div className="w-75 m-5 mx-auto">
        <div className="row g-5 justify-content-center">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body p-0">
                <table className="table table-bordered text-center mb-0 report-table">
                  <thead>
                    <tr>
                      <th className="bg-light">時間區間</th>
                      <th>CPA<br /><small>(人)</small></th>
                      <th>CMA<br /><small>(人)</small></th>
                      <th>EA<br /><small>(人)</small></th>
                      <th>CSU<br /><small>(科)</small></th>
                      <th>CPE<br /><small>(人)</small></th>
                      <th>CIA<br /><small>(人)</small></th>
                      <th>美稅<br /><small>(人)</small></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportRows.length > 0 ? (
                      reportRows.map((row, i) => (
                        <tr
                          key={i}
                          className={
                            row.period === '今年至今'
                              ? ''
                              : row.period.includes('去年')
                              ? ''
                              : ''
                          }
                        >
                          <td className="fw-bold">{row.period}</td>
                          <td>{row.cpa}</td>
                          <td>{row.cma}</td>
                          <td>{row.ea}</td>
                          <td>{row.csu}</td>
                          <td>{row.cpe}</td>
                          <td>{row.cia}</td>
                          <td>{row.tax}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-muted py-4">
                          尚無週報資料
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {reportData && data.map((item, i) => {
            const chartData = chartComputed.charts[i] ?? [];
            return (
              <div key={i} className="col-xl-4 col-md-6 col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="row g-1">
                      <div className="col-6 fw-bold">{item.title}</div>
                      <div className="col-6 text-end">
                        <select
                          className="form-select form-select-sm"
                          value={rangeMap[i]}
                          onChange={(e) => {
                            const updated = [...rangeMap];
                            updated[i] = e.target.value;
                            setRangeMap(updated);
                          }}
                        >
                          {timeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="col-8 text-start">
                        {item.type === "line"
                          ? new ChartLine().render(chartData)
                          : new ChartColumn().render(chartData)}
                      </div>

                      <div className="col-4 text-center">
                        {item.isRevenue ? (
                          <span style={{ fontSize: "35px" }}>
                            ${Number(reportSums?.[i] || 0).toLocaleString()}
                          </span>
                        ) : (
                          <>
                            <span style={{ fontSize: "60px" }}>
                              {Math.round(Number(reportSums?.[i] ?? 0))}
                            </span>
                            {item.unit}
                          </>
                        )}
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* 年度 KPI（今年至今 / 不重複成交會員數） */}
          <div className="col-xl-4 col-md-6 col-12">
            <div className="card">
              <div className="card-body">
                <div className="fw-bold mb-3">年度 KPI（今年至今）</div>

                {/* CPA */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <div className="fw-bold">CPA</div>
                    <div>{doneCPA} / {KPI.cpa} 人</div>
                  </div>
                  <div className="progress" role="progressbar" aria-valuenow={Math.round(pctCPA)} aria-valuemin="0" aria-valuemax="100">
                    <div className="progress-bar" style={{ width: `${pctCPA}%` }}></div>
                  </div>
                </div>

                {/* EA */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between">
                    <div className="fw-bold">EA</div>
                    <div>{doneEA} / {KPI.ea} 人</div>
                  </div>
                  <div className="progress" role="progressbar" aria-valuenow={Math.round(pctEA)} aria-valuemin="0" aria-valuemax="100">
                    <div className="progress-bar" style={{ width: `${pctEA}%` }}></div>
                  </div>
                </div>

                {/* CMA */}
                <div>
                  <div className="d-flex justify-content-between">
                    <div className="fw-bold">CMA</div>
                    <div>{doneCMA} / {KPI.cma} 人</div>
                  </div>
                  <div className="progress" role="progressbar" aria-valuenow={Math.round(pctCMA)} aria-valuemin="0" aria-valuemax="100">
                    <div className="progress-bar" style={{ width: `${pctCMA}%` }}></div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* 月 / 季 / 年 營業額（含加購） */}
          <div className="col-xl-4 col-md-6 col-12 mb-5">
            <div className="card">
              <div className="card-body">
                <div className="row g-1">
                  <div className="col-12 fw-bold">月營業額</div>
                  <div className="col-12 text-center">
                    <span style={{ fontSize: "60px" }}>
                       ${Number(monthRevenue || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-4 col-md-6 col-12 mb-5">
            <div className="card">
              <div className="card-body">
                <div className="row g-1">
                  <div className="col-12 fw-bold">季營業額</div>
                  <div className="col-12 text-center">
                    <span style={{ fontSize: "60px" }}>
                      ${Number(quarterRevenue || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-4 col-md-6 col-12 mb-5">
            <div className="card">
              <div className="card-body">
                <div className="row g-1">
                  <div className="col-12 fw-bold">年營業額</div>
                  <div className="col-12 text-center">
                    <span style={{ fontSize: "60px" }}>
                       ${Number(yearRevenue || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
export default AdmReport;
